import React, { useEffect, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useLocation, useParams } from "react-router-dom";
import { useFetchDataDetail, useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import ToastCst from "@components/ToastCst";
import { PagesID } from "@utils/utilsConfigurations";
import ButtonBackPage from "@components/ButtonBackPage";
import { Initiative as InitiativeInterface } from "src/interfaces/Initiative";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import useSWRMutation from "swr/mutation";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import {
  checkAssociationChanged,
  GetClusterData,
  LinkClusterBody,
} from "../types";

const AssociationDomain: React.FC<{
  page: PagesID;
}> = ({ page }) => {
  const { id, initiativeId, clusterId } = useParams();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["corporate.initiative"] + pathname}`;

  const [domainAssociation, setDomainAssociation] = useState<
    Record<number, boolean>
  >({});
  const { t } = useTranslation(["community-cluster-page", "toast-message"]);

  const { dataDetail: corporate } = useFetchDetail(
    ServicesURL.corporate,
    id || "",
    PagesID["corporate"],
  );

  const { dataDetail, mutate } = useFetchDataDetail(
    ServicesURL.getCommunityCluster
      .replace(":corporateId", id!)
      .replace(":initiativeId", initiativeId!)
      .replace(":clusterId", clusterId!),
    page,
  );

  const { trigger: triggerDomainAssociation } = useSWRMutation(
    ServicesURL.linkCommunityCluster
      .replace(":corporateId", id!)
      .replace(":initiativeId", initiativeId!)
      .replace(":clusterId", clusterId!),
    (url: string, { arg }: { arg: LinkClusterBody }) => {
      return fetcher<any>(`${url}`, MethodType.PUT, arg);
    },
  );

  const { columns, dataTable } = useFetchTable<InitiativeInterface[]>(
    ServicesURL.initiative + `/${id}/initiatives?status=ACTIVE`,
    PagesID["corporate.initiative"],
    filters[filterId],
  );

  useEffect(() => {
    if (dataDetail) {
      setDomainAssociation(
        ((dataDetail as GetClusterData).initiatives ?? []).reduce<
          Record<number, boolean>
        >((agg, cur) => {
          agg[cur.id] = true;
          return agg;
        }, {}),
      );
    }
  }, [dataDetail]);

  const handleRowSelection = async (domains: Record<number, boolean>) => {
    if (checkAssociationChanged(domainAssociation, domains)) {
      const domainIds = Object.entries(domains)
        .filter(([_, v]) => !!v)
        .map(([k]) => Number(k));
      const body: LinkClusterBody = {
        initiatives: domainIds,
      };
      await triggerDomainAssociation(body);
      mutate && mutate();
      setDomainAssociation(domains);
    }
  };

  const name = !Array.isArray(dataDetail as GetClusterData | [])
    ? (dataDetail as GetClusterData | undefined)?.cluster.name
    : "";

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-courses.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${name} - ${t("association-domain.hero-title")}`}
          description={t("association-domain.hero-description")}
        />
        <ButtonBackPage />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <Table
              id={PagesID["corporate.initiative"] + pathname}
              data={dataTable?.output}
              columns={columns || []}
              configurationTopToolbar={[]}
              enableRowSelection={true}
              enableRowActions={false}
              selectedRows={domainAssociation}
              onRowSelectionChange={(domains: any) =>
                handleRowSelection(domains)
              }
              getRowId={(originalRow: any) => originalRow?.id}
              totalPages={dataTable?.metadata?.total_elements}
              modAssociationEnabled={true}
            />
          </Box>
        )}
      </Container>
    </AnimationFadeIn>
  );
};

export default AssociationDomain;
